import { useState, useCallback } from 'react';
import { useAppDispatch } from '../../../../reduxService/hooks';
import * as DynamicProjectService from '../../../services/DynamicProjectService';
import { setProject } from '../../store/project/projectslice';
import { IDynamicProjectGetData, IProject } from '../../../api/IApiResponse';

/**
 * Hook for loading project data from API
 * Single responsibility: API fetching and loading state
 */
export const useProjectLoader = () => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>('');

    /**
     * Load project from API and optionally call onSuccess callback with loaded data.
     * The onSuccess callback allows callers to setup form immediately after load
     * without waiting for a React re-render cycle.
     */
    const loadProject = useCallback(
        async (
            projectId: string,
            transformFn: (data: IDynamicProjectGetData) => IProject,
            onSuccess?: (loadedProject: IProject) => void
        ): Promise<boolean> => {
            setLoading(true);
            setLoadError('');

            try {
                const apiData = await DynamicProjectService.getProject(projectId);
                const transformedData = transformFn(apiData);
                dispatch(setProject(transformedData));

                // Call onSuccess callback with loaded data - allows immediate form setup
                // 🔧 FIX: Await the callback to ensure form setup completes BEFORE loading=false
                // Without await, setLoading(false) in finally block runs BEFORE validatedData is set,
                // causing Formik to initialize with empty/default values instead of converted values
                if (onSuccess) {
                    await onSuccess(transformedData);
                }

                return true;
            } catch (error) {
                console.error('[PROJECT_LOADER] Error fetching project data:', error);
                setLoadError('error_fetching_project_data');
                return false;
            } finally {
                setLoading(false);
            }
        },
        [dispatch]
    );

    const clearError = useCallback(() => {
        setLoadError('');
    }, []);

    return {
        loading,
        loadError,
        loadProject,
        clearError,
        setLoading,
    };
};

export type UseProjectLoaderReturn = ReturnType<typeof useProjectLoader>;
